import type { Job } from '@/types';
import { mockHospitals } from './hospitals';

const today = new Date();
const inDays = (n: number) => new Date(today.getTime() + n * 86400000).toISOString().slice(0, 10);
const ago = (h: number) => new Date(today.getTime() - h * 3_600_000).toISOString();

export const mockJobs: Job[] = [
  {
    id: 'j_1',
    hospitalId: 'h_1',
    hospital: mockHospitals[0]!,
    title: 'ED Registrar — Overnight Shifts',
    specialty: 'Emergency Medicine',
    seniorityRequired: ['registrar', 'senior_registrar'],
    city: 'Sydney',
    state: 'NSW',
    postcode: '2065',
    rateAmount: 220,
    rateType: 'hour',
    startDate: inDays(7),
    endDate: inDays(35),
    jobType: 'locum',
    description:
      'Tier-1 trauma centre seeking senior ED registrar for 4-week block of overnight shifts. ED census ~280/day, full sub-specialty support on-site.',
    requirements: [
      'AHPRA general registration',
      'Minimum PGY5+ with ED experience',
      'ACEM trainee or equivalent preferred',
      'Australian work rights',
    ],
    benefits: ['Penalty rates after 22:00', 'Subsidised parking', 'On-site accommodation available'],
    accommodationProvided: true,
    travelProvided: false,
    status: 'active',
    applicationsCount: 14,
    viewsCount: 287,
    postedAt: ago(48),
    createdBy: 'p_recruiter_1',
    createdAt: ago(48),
    updatedAt: ago(2),
  },
  {
    id: 'j_2',
    hospitalId: 'h_2',
    hospital: mockHospitals[1]!,
    title: 'GP Locum — 2 Week Block',
    specialty: 'General Practice',
    seniorityRequired: ['gp'],
    city: 'Bendigo',
    state: 'VIC',
    postcode: '3550',
    rateAmount: 1850,
    rateType: 'day',
    startDate: inDays(14),
    endDate: inDays(28),
    jobType: 'locum',
    description:
      'Mixed billing GP clinic in Bendigo CBD. ~25 patients/day, full nursing & admin support. Accommodation + return flights provided.',
    requirements: [
      'FRACGP or equivalent',
      'AHPRA specialist registration in GP',
      'MMM 3+ eligibility advantageous',
    ],
    benefits: ['Return flights', 'Self-contained apartment', '70/30 mixed billing split'],
    accommodationProvided: true,
    travelProvided: true,
    status: 'active',
    applicationsCount: 8,
    viewsCount: 192,
    postedAt: ago(72),
    createdBy: 'p_recruiter_1',
    createdAt: ago(72),
    updatedAt: ago(6),
  },
  {
    id: 'j_3',
    hospitalId: 'h_4',
    hospital: mockHospitals[3]!,
    title: 'Rural Generalist — Far North QLD',
    specialty: 'Rural Generalist',
    seniorityRequired: ['gp', 'consultant'],
    city: 'Cairns',
    state: 'QLD',
    rateAmount: 2400,
    rateType: 'day',
    startDate: inDays(21),
    endDate: inDays(82),
    jobType: 'contract',
    description:
      '8-week rural generalist contract supporting outreach clinics across Cape York. Mix of ED, primary care and Indigenous health.',
    requirements: [
      'FACRRM or FRACGP with Rural Generalist endorsement',
      'Advanced skill in ED or anaesthetics',
      'Driver licence required',
    ],
    accommodationProvided: true,
    travelProvided: true,
    status: 'active',
    applicationsCount: 4,
    viewsCount: 96,
    postedAt: ago(120),
    createdBy: 'p_admin_1',
    createdAt: ago(120),
    updatedAt: ago(18),
  },
  {
    id: 'j_4',
    hospitalId: 'h_3',
    hospital: mockHospitals[2]!,
    title: 'O&G Consultant — Weekend Cover',
    specialty: 'Obstetrics & Gynaecology',
    seniorityRequired: ['consultant'],
    city: 'Brisbane',
    state: 'QLD',
    rateAmount: 280,
    rateType: 'hour',
    startDate: inDays(5),
    endDate: inDays(7),
    jobType: 'locum',
    description:
      'Weekend consultant cover at private maternity unit. ~6 deliveries / weekend, theatre access for emergency caesareans.',
    requirements: ['FRANZCOG', 'Current credentialling at private hospital preferred'],
    accommodationProvided: false,
    travelProvided: false,
    status: 'active',
    applicationsCount: 2,
    viewsCount: 41,
    postedAt: ago(12),
    createdBy: 'p_recruiter_1',
    createdAt: ago(12),
    updatedAt: ago(1),
  },
  {
    id: 'j_5',
    hospitalId: 'h_5',
    hospital: mockHospitals[4]!,
    title: 'Anaesthetics Registrar — 3 Month Term',
    specialty: 'Anaesthetics',
    seniorityRequired: ['registrar', 'senior_registrar'],
    city: 'Perth',
    state: 'WA',
    rateAmount: 9500,
    rateType: 'week',
    startDate: inDays(30),
    endDate: inDays(120),
    jobType: 'fixed_term',
    description:
      'Tertiary anaesthetic department. Mixed elective & emergency lists, regional & cardiac sub-specialty exposure.',
    requirements: ['ANZCA trainee or post-Part 1', 'AHPRA specialist or limited registration'],
    accommodationProvided: false,
    travelProvided: true,
    status: 'active',
    applicationsCount: 11,
    viewsCount: 224,
    postedAt: ago(160),
    createdBy: 'p_admin_1',
    createdAt: ago(160),
    updatedAt: ago(12),
  },
  {
    id: 'j_6',
    hospitalId: 'h_6',
    hospital: mockHospitals[5]!,
    title: 'Psychiatry Consultant — Hobart',
    specialty: 'Psychiatry',
    seniorityRequired: ['consultant'],
    city: 'Hobart',
    state: 'TAS',
    rateAmount: 260,
    rateType: 'hour',
    startDate: inDays(45),
    jobType: 'permanent',
    description:
      'Permanent FRANZCP psychiatrist position. Inpatient + community caseload, no on-call after hours.',
    requirements: ['FRANZCP', 'AHPRA specialist registration'],
    accommodationProvided: false,
    travelProvided: false,
    status: 'active',
    applicationsCount: 3,
    viewsCount: 67,
    postedAt: ago(200),
    createdBy: 'p_recruiter_1',
    createdAt: ago(200),
    updatedAt: ago(24),
  },
];
